import { useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, X } from 'lucide-react';
import { NoDeliveryCauseReason, type DefectCauseInput, type WorkItemView } from '@vwt/contracts';
import { Button, Dialog, DialogContent, DialogFooter, Field, Input, NativeSelect, Textarea, toast } from '@vwt/ui';
import { isApiError, newIdempotencyKey, queryClient, useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { KeyLabel } from '../../components/badges.js';
import { Choice } from '../documents/access-dialog.js';

type WorkRef = { id: string; key: string; title: string };

/** Work search shared by the relation picker and the cause form; archived=any finds closed, archived origins. */
export function WorkSearchList({ excludeId, onPick, selectedIds = [], archived, placeholder, autoFocus }: { excludeId: string; onPick: (w: WorkRef) => void; selectedIds?: string[]; archived?: 'any'; placeholder: string; autoFocus?: boolean }) {
  const [q, setQ] = useState('');
  const search = useApiQuery('listWorkItems', { query: { q, limit: '8', includeCanceled: 'true', ...(archived ? { archived } : {}) } }, { enabled: q.trim().length >= 2 });
  return (
    <div className="flex flex-col gap-1">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} aria-label={placeholder} autoFocus={autoFocus} />
      <ul className="max-h-56 overflow-y-auto">
        {(search.data?.data ?? [])
          .filter((w) => w.id !== excludeId)
          .map((w) => (
            <li key={w.id}>
              <button
                type="button"
                aria-pressed={selectedIds.includes(w.id)}
                onClick={() => onPick({ id: w.id, key: w.key, title: w.title })}
                className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted ${selectedIds.includes(w.id) ? 'bg-accent-soft' : ''}`}
              >
                <KeyLabel value={w.key} />
                <span className="truncate">{w.title}</span>
              </button>
            </li>
          ))}
      </ul>
    </div>
  );
}

export interface CauseDraft {
  kind: 'CAUSED_BY' | 'NO_DELIVERY_CAUSE';
  targets: WorkRef[];
  reason: NoDeliveryCauseReason;
  note: string;
}

export const emptyCause = (item: WorkItemView): CauseDraft => ({ kind: 'CAUSED_BY', targets: [], reason: item.noDeliveryCause?.reason ?? 'PRE_EXISTING', note: item.noDeliveryCause?.note ?? '' });

/** The cause the draft names, or null while it is incomplete. */
export function causeInput(draft: CauseDraft): DefectCauseInput | null {
  if (draft.kind === 'CAUSED_BY') return draft.targets.length ? { kind: 'CAUSED_BY', workItemIds: draft.targets.map((w) => w.id) } : null;
  const note = draft.note.trim();
  if (draft.reason === 'OTHER' && !note) return null;
  return { kind: 'NO_DELIVERY_CAUSE', reason: draft.reason, ...(note ? { note } : {}) };
}

/** "Caused by" (up to five items) or "No delivery cause" with a reason; a note is required for Other. */
export function CauseFields({ item, value, onChange }: { item: WorkItemView; value: CauseDraft; onChange: (draft: CauseDraft) => void }) {
  const { t } = useTranslation();
  const toggle = (w: WorkRef) => {
    const has = value.targets.some((x) => x.id === w.id);
    if (!has && value.targets.length >= 5) return;
    onChange({ ...value, targets: has ? value.targets.filter((x) => x.id !== w.id) : [...value.targets, w] });
  };
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-xs font-medium text-fg-muted">
        {t('work.defectCause.title')} <span className="text-danger">*</span>
      </legend>
      <Choice name={`cause-${item.id}`} value="CAUSED_BY" current={value.kind} onSelect={(kind) => onChange({ ...value, kind })} label={t('work.defectCause.causedBy')} help={t('work.defectCause.causedByHelp')} />
      {value.kind === 'CAUSED_BY' ? (
        <div className="flex flex-col gap-2 pl-6">
          {value.targets.length ? (
            <ul className="flex flex-wrap gap-1">
              {value.targets.map((w) => (
                <li key={w.id} className="flex max-w-full items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs">
                  <KeyLabel value={w.key} />
                  <span className="max-w-40 truncate">{w.title}</span>
                  <button type="button" aria-label={`${t('common.delete')} ${w.key}`} className="text-fg-subtle hover:text-danger" onClick={() => toggle(w)}>
                    <X className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <WorkSearchList excludeId={item.id} onPick={toggle} selectedIds={value.targets.map((w) => w.id)} archived="any" placeholder={t('work.defectCause.search')} />
        </div>
      ) : null}
      <Choice name={`cause-${item.id}`} value="NO_DELIVERY_CAUSE" current={value.kind} onSelect={(kind) => onChange({ ...value, kind })} label={t('work.defectCause.none')} help={t('work.defectCause.noneHelp')} />
      {value.kind === 'NO_DELIVERY_CAUSE' ? (
        <div className="flex flex-col gap-2 pl-6">
          <Field label={t('work.defectCause.category')} htmlFor={`cause-reason-${item.id}`} required>
            <NativeSelect id={`cause-reason-${item.id}`} value={value.reason} onChange={(e) => onChange({ ...value, reason: e.target.value as NoDeliveryCauseReason })}>
              {NoDeliveryCauseReason.options.map((r) => (
                <option key={r} value={r}>
                  {t(`work.defectCause.reasons.${r}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label={t('work.defectCause.note')} htmlFor={`cause-note-${item.id}`} required={value.reason === 'OTHER'}>
            <Textarea id={`cause-note-${item.id}`} value={value.note} onChange={(e) => onChange({ ...value, note: e.target.value })} placeholder={t('work.defectCause.notePlaceholder')} rows={2} maxLength={500} />
          </Field>
        </div>
      ) : null}
    </fieldset>
  );
}

/** Records the cause on the bug page in one request, so several links commit together or not at all (ADR-0059). */
function useRecordCause(item: WorkItemView) {
  const { t } = useTranslation();
  const update = useApiMutation('updateWorkItem', { invalidate: ['getWorkItem', 'listWorkItems'] });
  const record = async (defectCause: DefectCauseInput | null) => {
    try {
      await update.mutateAsync({ params: { id: item.id }, body: { expectedVersion: item.version, defectCause }, idempotencyKey: newIdempotencyKey() });
      toast.success(t('work.defectCause.saved'));
      return true;
    } catch (error) {
      // A conflict reloads the bug so the next save uses its latest version; the message says why.
      if (isApiError(error) && error.code === 'VERSION_CONFLICT') await queryClient.invalidateQueries({ queryKey: ['getWorkItem'] });
      toastError(error);
      return false;
    }
  };
  return { record, pending: update.isPending };
}

/** Classifies a bug after the fact, for bugs closed by production evidence or imported (ADR-0059). */
function CauseDialog({ item, onClose }: { item: WorkItemView; onClose: () => void }) {
  const { t } = useTranslation();
  const { record, pending } = useRecordCause(item);
  const [draft, setDraft] = useState(() => ({ ...emptyCause(item), kind: item.defectCause === 'NO_DELIVERY_CAUSE' ? ('NO_DELIVERY_CAUSE' as const) : ('CAUSED_BY' as const) }));
  const cause = causeInput(draft);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={t(item.defectCause === 'NOT_INFORMED' ? 'work.defectCause.inform' : 'work.defectCause.changeTitle')} description={t('work.defectCause.notInformedHint')} closeLabel={t('common.close')}>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (cause) void record(cause).then((saved) => saved && onClose());
          }}
        >
          <CauseFields item={item} value={draft} onChange={setDraft} />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={pending} disabled={!cause}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Banner on a closed bug that names no cause, with the way to add it. */
export function CauseNotInformedBanner({ item, canEdit }: { item: WorkItemView; canEdit: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-[var(--radius)] bg-warning-soft px-3 py-2 text-xs text-warning">
      <AlertTriangle className="size-3.5 shrink-0" />
      <span className="min-w-0 flex-1">
        <strong className="font-medium">{t('work.defectCause.notInformed')}.</strong> {t('work.defectCause.notInformedHint')}
      </span>
      {canEdit ? (
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
          {t('work.defectCause.inform')}
        </Button>
      ) : null}
      {open ? <CauseDialog item={item} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

/** The bug's cause in its properties: the caused-by links, "no delivery cause" with its reason, or not recorded. */
export function CauseValue({ item, canEdit }: { item: WorkItemView; canEdit: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { record } = useRecordCause(item);
  // Links are removed here too, so an archived bug (no relation editing) can still correct a wrong cause.
  const unlink = useApiMutation('removeWorkRelation', { invalidate: ['getWorkItem'] });
  const causes = item.relations.filter((r) => r.kind === 'CAUSED_BY' && r.direction === 'OUTGOING');
  return (
    <div className="flex flex-col items-start gap-0.5 text-sm">
      {item.defectCause === 'CAUSED' ? (
        causes.length ? (
          causes.map((r) => (
            <span key={r.id} className="flex items-center gap-1">
              <Link to={`/work/${r.other.key}`} className="hover:underline">
                <KeyLabel value={r.other.key} />
              </Link>
              {canEdit ? (
                <button type="button" aria-label={`${t('common.delete')} ${r.other.key}`} className="text-fg-subtle hover:text-danger" onClick={() => void unlink.mutateAsync({ params: { id: item.id, childId: r.id } }).catch(toastError)}>
                  <X className="size-3" />
                </button>
              ) : null}
            </span>
          ))
        ) : (
          <span>{t('work.defectCause.causedBy')}</span>
        )
      ) : item.defectCause === 'NO_DELIVERY_CAUSE' && item.noDeliveryCause ? (
        <>
          <span>{t('work.defectCause.noDeliveryCause', { reason: t(`work.defectCause.reasons.${item.noDeliveryCause.reason}`) })}</span>
          {item.noDeliveryCause.note ? <span className="text-xs text-fg-subtle">{item.noDeliveryCause.note}</span> : null}
          {canEdit ? (
            <span className="flex gap-2 text-xs">
              <button type="button" className="text-accent hover:underline" onClick={() => setOpen(true)}>
                {t('work.defectCause.change')}
              </button>
              {/* A closed bug only changes its cause: clearing it would drop it back to "not recorded" in one click. */}
              {item.phase !== 'DONE' ? (
                <button type="button" className="text-fg-subtle hover:text-danger" onClick={() => void record(null)}>
                  {t('work.defectCause.clear')}
                </button>
              ) : null}
            </span>
          ) : null}
        </>
      ) : (
        <>
          <span className="text-fg-subtle">{t('work.defectCause.notInformed')}</span>
          {canEdit ? (
            <button type="button" className="text-xs text-accent hover:underline" onClick={() => setOpen(true)}>
              {t('work.defectCause.inform')}
            </button>
          ) : null}
        </>
      )}
      {open ? <CauseDialog item={item} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}
